import StakeInitialization from "./staking";
import { fromSatoshi, toSatoshi, Hex } from "../lib/utils";
import { DepositStatus } from "../lib/api/TbtcApi";
/**
 * Module exposing features related to the account.
 */
export default class Account {
    /**
     * Acre contracts.
     */
    #contracts;
    /**
     * tBTC Module.
     */
    #tbtc;
    /**
     * Acre subgraph api.
     */
    #acreSubgraphApi;
    #bitcoinAddress;
    #ethereumAddress;
    #bitcoinPublicKey;
    #bitcoinProvider;
    #orangeKitSdk;
    constructor(contracts, tbtc, acreSubgraphApi, account, bitcoinProvider, orangeKitSdk) {
        this.#contracts = contracts;
        this.#tbtc = tbtc;
        this.#acreSubgraphApi = acreSubgraphApi;
        this.#bitcoinAddress = account.bitcoinAddress;
        this.#ethereumAddress = account.ethereumAddress;
        this.#bitcoinProvider = bitcoinProvider;
        this.#orangeKitSdk = orangeKitSdk;
        this.#bitcoinPublicKey = account.bitcoinPublicKey;
    }
    /**
     * Initializes the Acre deposit process.
     * @param referral Data used for referral program.
     * @param bitcoinRecoveryAddress `P2PKH` or `P2WPKH` Bitcoin address that can
     *        be used for emergency recovery of the deposited funds. If
     *        `undefined` the bitcoin address from bitcoin provider is used as
     *        bitcoin recovery address - note that an address returned by bitcoin
     *        provider must then be `P2WPKH` or `P2PKH`. This property is
     *        available to let the consumer use `P2SH-P2WPKH` as the deposit owner
     *        and another tBTC-supported type (`P2WPKH`, `P2PKH`) address as the
     *        tBTC Bridge recovery address.
     * @returns Object represents the deposit process.
     */
    async initializeStake(referral, bitcoinRecoveryAddress) {
        // tBTC-v2 SDK will handle Bitcoin address validation and throw an error if
        // address is not supported.
        const finalBitcoinRecoveryAddress = bitcoinRecoveryAddress ?? this.#bitcoinAddress;
        const tbtcDeposit = await this.#tbtc.initiateDeposit(this.#ethereumAddress, finalBitcoinRecoveryAddress, referral, this.#bitcoinAddress);
        return new StakeInitialization(tbtcDeposit);
    }
    /**
     * @returns Balance of the account's acreBTC shares (in 1e18 precision).
     */
    async sharesBalance() {
        return this.#contracts.acreBTC.balanceOf(this.#ethereumAddress);
    }
    /**
     * @returns Balance of Bitcoin position in Acre estimated based on the
     *          account's acreBTC shares (in 1e8 satoshi precision).
     */
    async estimatedBitcoinBalance() {
        return toSatoshi(await this.#contracts.acreBTC.assetsBalanceOf(this.#ethereumAddress));
    }
    /**
     * @returns All deposits associated with the account. They include all
     *          deposits: queued, initialized and finalized.
     */
    async getDeposits() {
        const subgraphData = await this.#acreSubgraphApi.getDepositsByOwner(this.#ethereumAddress);
        const initializedOrFinalizedDepositsMap = new Map(subgraphData
            .filter((d) => d.status !== DepositStatus.Migrated)
            .map((data) => [data.depositKey, data]));
        const tbtcData = await this.#tbtc.getDepositsByOwner(this.#ethereumAddress);
        const migratedDeposits = subgraphData
            .filter((d) => d.status === DepositStatus.Migrated)
            .map((migratedDeposit) => ({
            // In that case this is not actually the deposit key.It's a migrated
            // deposit and the id is `<txHash>_<log_index>`.
            id: migratedDeposit.depositKey,
            // For migrated deposit the bitcoin tx hash is null.
            txHash: migratedDeposit.txHash,
            amount: toSatoshi(migratedDeposit.amountToDeposit),
            status: migratedDeposit.status,
            initializedAt: migratedDeposit.initializedAt,
            finalizedAt: migratedDeposit.initializedAt,
        }));
        return tbtcData
            .map((deposit) => {
            const depositFromSubgraph = initializedOrFinalizedDepositsMap.get(deposit.depositKey);
            const amount = toSatoshi(depositFromSubgraph?.amountToDeposit || deposit.initialAmount);
            return {
                id: deposit.depositKey,
                txHash: deposit.txHash,
                amount,
                status: deposit.status,
                initializedAt: deposit.initializedAt,
                finalizedAt: depositFromSubgraph?.finalizedAt,
            };
        })
            .concat(migratedDeposits);
    }
    /**
     * Initializes the withdrawal process.
     * @param amount Bitcoin amount to withdraw in 1e8 satoshi precision.
     * @param dataBuiltStepCallback A callback triggered after the data
     *        building step.
     * @param onSignMessageStepCallback A callback triggered before the message
     *        signing step.
     * @param messageSignedStepCallback A callback triggered after the message
     *        signing step.
     * @returns Hash of the withdrawal transaction and the redemption request id.
     */
    async initializeWithdrawal(btcAmount, dataBuiltStepCallback, onSignMessageStepCallback, messageSignedStepCallback) {
        const tbtcAmount = fromSatoshi(btcAmount);
        const shares = await this.#contracts.acreBTC.convertToShares(tbtcAmount);
        const safeTxData = this.#contracts.acreBTC.encodeApproveAndCallFunctionData(this.#contracts.bitcoinRedeemer.getChainIdentifier(), shares, this.#tbtc.buildRedemptionData(this.#ethereumAddress, this.#bitcoinAddress));
        await dataBuiltStepCallback?.(safeTxData);
        const transactionHash = await this.#orangeKitSdk.sendTransaction(`0x${this.#contracts.acreBTC.getChainIdentifier().identifierHex}`, "0x0", safeTxData.toPrefixedString(), this.#bitcoinAddress, this.#bitcoinPublicKey, async (message, txData) => {
            await onSignMessageStepCallback?.(message);
            const signedMessage = await (this.#bitcoinProvider.signWithdrawMessage?.(message, txData) ??
                (await this.#bitcoinProvider.signMessage(message)));
            await messageSignedStepCallback?.(signedMessage);
            return signedMessage;
        });
        const redemptionRequestId = await this.#contracts.bitcoinRedeemer.findRedemptionRequestIdFromTransaction(Hex.from(transactionHash));
        return { transactionHash, redemptionRequestId };
    }
    /**
     * @returns All withdrawals associated with the account.
     */
    async getWithdrawals() {
        return (await this.#acreSubgraphApi.getWithdrawalsByOwner(this.#ethereumAddress)).map((withdraw) => {
            let status = "requested";
            if (withdraw.initializedAt)
                status = "initialized";
            if (withdraw.finalizedAt)
                status = "finalized";
            return {
                ...withdraw,
                amount: toSatoshi(withdraw.amount),
                requestedAmount: toSatoshi(withdraw.requestedAmount),
                status,
            };
        });
    }
}
//# sourceMappingURL=account.js.map